// @noflow

/**
 * PurchaseButton component
 *
 * This component renders a button that allows users to create a subscription
 * without providing payment details. It is intended for scenarios where nothing
 * is payable at checkout, e.g. when a 100% discount is applied.
 *
 * We are unable to use StripePaymentElement as it requires a payable amount
 * above 0 to render. In turn, we cannot use buySubscriptionWithStripe as it
 * requires a mounted Stripe Element, and thus we need to use
 * buySubscriptionWithCard instead.
 *
 * @example
 * // To use this component, import it and use it in your component's render method
 * ensuring to wrap it with the Elements component from Stripe:
 *
 *    <Elements
 *     stripe={stripePromise}
 *     key={stripeKey}
 *     options={{
 *       locale: localeToStripeLocale(language)
 *     }}
 *   >
 *     <PurchaseButton
 *       namespace={namespace}
 *       shippingCountryCode={shippingCountryCode}
 *       affiliateType={affiliateType}
 *       csrfToken={csrfToken}
 *     />
 *   </Elements>
 */
import { useReactiveVar } from '@apollo/client'
import { useElements, useStripe } from '@stripe/react-stripe-js'
import React, { useCallback } from 'react'

import { Button } from '@/components/elements/atoms/Button'

import type { CheckoutPage } from '../../queries/__generated__/CheckoutPage'
import { Code as CountryCode } from '@/types'

import { checkoutPageState } from '../../CheckoutPage'
import { paymentSubmissionState } from './StripePaymentElement'
import buySubscriptionWithCard from './helpers/buySubscriptionWithCard'
import usePaymentView from './hooks/usePaymentView'

type Props = {
  namespace: string
  affiliateType: CheckoutPage['guest']['affiliateType']
  shippingCountryCode: CountryCode
  csrfToken: string
}

const PurchaseButton = ({
  namespace,
  affiliateType,
  shippingCountryCode,
  csrfToken
}: Props): JSX.Element | null => {
  const submissionState = useReactiveVar(paymentSubmissionState)
  const { sections, user } = checkoutPageState()
  const { discount } = user
  const stripe = useStripe()
  const elements = useElements()

  const hasFreeBox =
    discount?.discountBasis === 'percentage' && discount?.value === 100

  const areAllSectionsValid = () =>
    Object.values(sections).every(({ valid }) => valid)

  const { activePaymentView } = usePaymentView({
    affiliateType,
    shippingCountryCode,
    requiresPayment: false,
    paymentRequest: null
  })

  const buySubscriptionCallback = useCallback(() => {
    if (!stripe || !elements) {
      return
    }
    buySubscriptionWithCard({ stripe, elements, csrfToken })
  }, [csrfToken, elements, stripe])

  if (!hasFreeBox) {
    return null
  }

  return (
    <div className="checkout__continue-button checkout__continue-button--purchase">
      <Button
        typography={{
          namespace,
          text:
            submissionState.type === 'loading'
              ? 'continue_button.loading'
              : 'continue_button.order'
        }}
        disabled={
          submissionState.type === 'loading' ||
          (!areAllSectionsValid() && activePaymentView !== 'none')
        }
        onClick={buySubscriptionCallback}
        fullWidth
        disableAnalytics
      />
    </div>
  )
}

export default PurchaseButton
